import React from "react";
import { Clock3, CheckCircle2, AlertCircle } from "lucide-react";
import { fmtD, fmtDT } from "../../lib/date";

const name = (channel, zh) => channel === "sms" ? (zh ? "短信" : "SMS") : (zh ? "邮件" : "Email");
const todayPT = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

// 只采用后端给出的工作日日期；浏览器所在时区不能改变同一条催款的冷却期。
export function collectionChannelBlock(items, channel, zh, receipt = false, date = todayPT()) {
  if (receipt) return "";
  for (const item of items || []) {
    const state = item.collection;
    if (!state) continue;
    if (state.unknownChannels?.[channel] || state.unknownChannels?.includes?.(channel)) return zh ? "发送结果待核对，暂不能重发" : "Delivery outcome unknown; verification required";
    if (state.activeAttemptId) return zh ? "同事正在发送，请稍后刷新" : "A colleague is sending; refresh shortly";
    if (["settled", "cancelled", "waiting_invoice"].includes(state.phase) || state.outstandingCents === 0) return zh ? "该期已结清、撤销或尚未开票" : "Settled, cancelled or not yet invoiced";
    const eligible = state.nextEligibleDate?.[channel];
    if (eligible && date < eligible) return zh ? `已发送，${fmtD(eligible)} 起可再次提醒` : `Already sent; available again ${fmtD(eligible)}`;
  }
  return "";
}

export function newCollectionRequestId() {
  return globalThis.crypto?.randomUUID?.() || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const n = Math.floor(Math.random() * 16); return (c === "x" ? n : (n & 3) | 8).toString(16);
  });
}

export function collectionError(error, zh) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || error?.detailText || error?.message || (zh ? "操作失败，请刷新后重试" : "Request failed; refresh and retry");
}

export function resultLocks(current, result, zh) {
  const next = { ...current };
  for (const channel of result.sent || []) next[channel] = zh ? "本次已成功发送" : "Sent successfully in this session";
  for (const outcome of result.outcomes || []) {
    if (outcome.status === "unknown") next[outcome.channel] = zh ? "发送结果待核对，暂不能重发" : "Delivery outcome unknown; verification required";
  }
  return next;
}

export function deliverySummary(result, zh) {
  const sent = (result.sent || []).map(c => name(c, zh));
  const outcomes = result.outcomes || [];
  const unknown = outcomes.filter(o => o.status === "unknown").map(o => name(o.channel, zh));
  const failures = { ...result.skipped };
  for (const outcome of outcomes) if (["failed", "skipped"].includes(outcome.status) && !failures[outcome.channel]) failures[outcome.channel] = outcome.reason || outcome.error || (zh ? "未成功发送" : "Delivery failed");
  const missing = Object.entries(failures).filter(([channel]) => !outcomes.some(o => o.channel === channel && o.status === "unknown"))
    .map(([channel, reason]) => `${name(channel, zh)}：${reason}`);
  const parts = [];
  if (sent.length) parts.push(zh ? `已发送：${sent.join("、")}` : `Sent: ${sent.join(", ")}`);
  if (missing.length) parts.push(zh ? `未发送：${missing.join("；")}` : `Not sent: ${missing.join("; ")}`);
  if (unknown.length) parts.push(zh ? `结果待核对：${unknown.join("、")}，请勿重发` : `Outcome unknown: ${unknown.join(", ")}; do not resend`);
  if (!parts.length) parts.push(zh ? "未成功发送，请核对联系方式或服务状态" : "Nothing sent; check contact details and service status");
  return { text: parts.join(zh ? "；" : "; "), complete: sent.length > 0 && !missing.length && !unknown.length };
}

export function CollectionDeliveryResult({ result, zh, testId }) {
  if (!result) return null;
  const summary = deliverySummary(result, zh);
  return <div role="status" className={`rounded-lg border px-3 py-2 text-xs leading-relaxed ${summary.complete ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}
    data-testid={testId} data-ok={summary.complete ? 1 : 0} data-missed={summary.complete ? 0 : 1}>{summary.text}</div>;
}

export function CollectionFollowupStatus({ item, zh = true, compact = false }) {
  const state = item.collection;
  const notice = item.notice || {};
  const successful = state?.lastSuccessByChannel || { sms: notice.smsAt, email: notice.emailAt };
  const actor = typeof state?.lastActor === "object" ? state.lastActor?.name || state.lastActor?.username : state?.lastActor;
  const sent = Object.entries(successful).filter(([, at]) => at);
  if (!state && !sent.length) return null;
  const phaseLabel = {
    waiting_invoice: zh ? "待正式开票" : "Awaiting invoice",
    waiting_send: zh ? "待首次发送" : "Awaiting first reminder",
    waiting_due_date: zh ? "已提醒 · 待财务补充到期日" : "Reminder sent · finance must confirm the due date",
    follow_up: zh ? "跟进中" : "Following up",
    manager7: zh ? "市场部经理跟进 · 7 工作日" : "Marketing manager follow-up · 7 business days",
    vp10: zh ? "销售 VP 跟进 · 工程团队评估" : "Sales VP follow-up · engineering review",
    settled: zh ? "已结清" : "Settled", cancelled: zh ? "已撤销" : "Cancelled",
  }[state?.phase];
  const unknown = Object.keys(state?.unknownChannels || {});
  const Icon = unknown.length ? AlertCircle : sent.length ? CheckCircle2 : Clock3;
  return <div className={`${compact ? "text-xs mt-1" : "rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs"} space-y-1 leading-relaxed`} data-testid={`collection-status-${item.key || item.coId || "payment"}`}>
    {phaseLabel && <p className="inline-flex items-center gap-1.5 font-medium text-slate-700"><Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />{phaseLabel}</p>}
    {sent.map(([channel, at]) => <p key={channel} className="text-emerald-700 break-words">{name(channel, zh)} · {zh ? "成功发送" : "Sent"} {fmtDT(at)}</p>)}
    {sent.length > 0 && actor && <p className="text-slate-500">{zh ? "最近发送人" : "Most recent sender"} · {actor}</p>}
    {state?.anchorDate && !["settled", "cancelled"].includes(state.phase) && <p className="text-slate-500">{zh ? "计时起点" : "Follow-up starts"} {fmtD(state.anchorDate)} · {state.businessDays ?? 0} {zh ? "个工作日" : "business days"}</p>}
    {!compact && state && !["settled", "cancelled"].includes(state.phase) && <p className="text-slate-500">{zh ? "7 个工作日：市场部经理；10 个工作日：销售 VP、工程经理及线上／现场项目经理。" : "7 business days: marketing manager. 10: sales VP, engineering manager and online/site PMs."}</p>}
    {unknown.length > 0 && <p className="text-amber-700">{zh ? "发送结果待核对，暂不能重发" : "Delivery outcome unknown; verify before resending"} · {unknown.map(c => name(c, zh)).join(" / ")}</p>}
  </div>;
}
